/**
 * AuthMiddleware — thin route guard
 *
 * Previously this file owned its own auth state, ran initializeAuth(),
 * had an 8-second loading timeout, and pushed updates into AuthContext
 * via an onAuthStateChange prop. That created a race window where
 * ChatbotList rendered with `user: null` while AuthMiddleware's
 * initializer was still running, AND the loading timeout would force
 * `isLoading: false` with `user: null` after 8 seconds — caching that
 * broken state into localStorage and breaking subsequent visits.
 *
 * NOW: AuthContext owns all auth state and subscribes to Supabase auth
 * events directly. This file is purely a route guard — it READS from
 * AuthContext via useAuth() and redirects if the current path isn't
 * accessible. Zero state of its own.
 */
import { useEffect, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { User, Session } from '@supabase/supabase-js';
import { useAuth } from '@/contexts/AuthContext';

// Kept for backward compatibility — other files import these types.
interface UserProfile {
  id: string;
  email: string;
  name: string | null;
  role: string | null;
  subscription_status: string | null;
  is_banned: boolean | null;
  created_at: string;
  updated_at: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  subscription_id: string | null;
  price_id: string | null;
  plan_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  last_payment_date: string | null;
}

interface AuthState {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  isAdmin: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthMiddlewareProps {
  children: ReactNode;
  // Kept in the type for backward compat with App.tsx, but not used.
  // AuthContext now owns auth state — no callback needed.
  onAuthStateChange?: (authState: AuthState) => void;
}

const PUBLIC_ROUTE_PREFIXES = [
  '/portal/auth',
  '/portal/signup',
  '/pricing',
  '/chat/',
  '/fullscreen-chat/',
  '/c/',         // standalone campaign pages
  '/try',        // live demo / scoring widget
  '/terms',
  '/privacy',
  '/docs',
];

function isPublicRoute(path: string): boolean {
  if (path === '/') return true;
  return PUBLIC_ROUTE_PREFIXES.some((prefix) => path.startsWith(prefix));
}

const AuthMiddleware = ({ children }: AuthMiddlewareProps) => {
  const { isAuthenticated, isAdmin, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // While AuthContext is still hydrating its first session check, do
    // nothing — let components render their loading states. We never
    // redirect during the loading window because we don't yet know if
    // the user is logged in.
    if (isLoading) return;

    const path = location.pathname;

    // Public routes are always accessible
    if (isPublicRoute(path)) return;

    // Protected route + not authenticated → bounce to login
    if (!isAuthenticated) {
      navigate('/portal/auth', { replace: true });
      return;
    }

    // Admin route + not admin → bounce to portal
    if (path.startsWith('/admin') && !isAdmin) {
      navigate('/portal', { replace: true });
      return;
    }
  }, [isAuthenticated, isAdmin, isLoading, location.pathname, navigate]);

  // Always render children — components handle their own loading states.
  return <>{children}</>;
};

export default AuthMiddleware;
export type { AuthState, UserProfile };
